import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { ConfigProvider } from "antd";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { getAntdLocale } from "util/antdLocale";
import darkAlgorithm from "antd/es/theme/themes/dark";
import ConsoleSideMenu from "../helperComponents/ConsoleSideMenu.js";
import "../stylesheets/consoleLayout.css";
import DashboardGuide from "./DashboardGuide/DashboardGuide.js";
import { themeLayoutColor } from "util/common.js";
import { BRAND, DARK, brandAlpha } from "theme/tokens";
import GlobalCopilotPanel from "components/Copilot/GlobalCopilotPanel";
import AdvisorIcon from "components/Copilot/AdvisorIcon";
import { CopilotShellProvider } from "contexts/CopilotShellContext";
import { checkCxGptPlanAccess } from "util/cxGptAccess";
import AdvisorPopup from "components/AdvisorPopup";

const COPILOT_PANEL_WIDTH_KEY = "pivony_copilot_panel_width";
const CONSOLE_SIDEBAR_COLLAPSED_KEY = "pivony_console_sidebar_collapsed";

const readStoredSidebarCollapsed = () => {
  try {
    return localStorage.getItem(CONSOLE_SIDEBAR_COLLAPSED_KEY) === "1";
  } catch (_) {}
  return false;
};

// Memoized wrapper so sidebar collapse/expand does not re-render page content
const StableContent = React.memo(({ children, advisorFullPage, prospectPage }) => (
  <div
    className={`consoleLayoutRoot-content${
      advisorFullPage ? " consoleLayoutRoot-content--advisor" : ""
    }${prospectPage ? " consoleLayoutRoot-content--prospect" : ""}`}
  >
    {children}
  </div>
));
const COPILOT_WIDTH_MIN = 360;
const COPILOT_WIDTH_MAX = 560;
const COPILOT_WIDTH_DEFAULT = 400;

const readStoredCopilotWidth = () => {
  try {
    const w = parseInt(localStorage.getItem(COPILOT_PANEL_WIDTH_KEY), 10);
    if (Number.isFinite(w) && w >= COPILOT_WIDTH_MIN && w <= COPILOT_WIDTH_MAX) {
      return w;
    }
  } catch (_) {}
  return COPILOT_WIDTH_DEFAULT;
};

const ConsoleLayout = ({ children, ...props }) => {
  const { i18n, t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const antdLocale = useMemo(
    () => getAntdLocale(i18n.language),
    [i18n.language],
  );
  const isAdvisorFullPage =
    location.pathname === "/console/advisor" ||
    location.pathname === "/console";
  const isProspectPage = location.pathname.startsWith("/console/sonic-prospect");
  const [openRightClickPopup, setOpenRightClickPopup] = useState(false);
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(
    () => !readStoredSidebarCollapsed()
  );
  const [copilotOpen, setCopilotOpen] = useState(false);
  const toggleSidebarCollapse = useCallback(() => {
    setLeftSidebarOpen((open) => {
      const nextOpen = !open;
      try {
        localStorage.setItem(
          CONSOLE_SIDEBAR_COLLAPSED_KEY,
          nextOpen ? "0" : "1"
        );
      } catch (_) {}
      return nextOpen;
    });
  }, []);
  const [copilotWidth, setCopilotWidth] = useState(readStoredCopilotWidth);
  const [cxGptAccess, setCxGptAccess] = useState({
    loading: true,
    hasAccess: false,
    advisorPlan: null,
    advisorPlanName: null,
    advisorIsPro: false,
    advisorTrial: null,
  });
  const widthRef = useRef(copilotWidth);
  const theme = props.theme || "dark";

  useEffect(() => {
    let cancelled = false;
    checkCxGptPlanAccess().then((result) => {
      if (cancelled) return;
      setCxGptAccess({
        loading: false,
        hasAccess: result.hasAccess === true,
        advisorPlan: result.advisorPlan || null,
        advisorPlanName: result.advisorPlanName || null,
        advisorIsPro: result.isPro === true,
        advisorTrial: result.trial || null,
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    widthRef.current = copilotWidth;
  }, [copilotWidth]);

  const persistCopilotWidth = useCallback(() => {
    try {
      localStorage.setItem(COPILOT_PANEL_WIDTH_KEY, String(widthRef.current));
    } catch (_) {}
  }, []);

  const onCopilotResizeStart = useCallback(
    (e) => {
      e.preventDefault();
      const startX = e.clientX;
      const startW = widthRef.current;
      const onMove = (ev) => {
        const delta = startX - ev.clientX;
        const next = Math.min(
          COPILOT_WIDTH_MAX,
          Math.max(COPILOT_WIDTH_MIN, startW + delta)
        );
        setCopilotWidth(next);
      };
      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        persistCopilotWidth();
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [persistCopilotWidth]
  );

  /** Cycle preset widths (toolbar expand). */
  const onCopilotToggleWide = useCallback(() => {
    setCopilotWidth((w) => {
      const next = w < 400 ? 440 : w < 500 ? COPILOT_WIDTH_MAX : COPILOT_WIDTH_MIN;
      widthRef.current = next;
      try {
        localStorage.setItem(COPILOT_PANEL_WIDTH_KEY, String(next));
      } catch (_) {}
      return next;
    });
  }, []);

  /** Light/brand: default Tooltip uses dark “spotlight” — unreadable on pastel sidebar hover */
  const consoleAntdTheme = useMemo(
    () =>
      theme === "dark"
        ? {
            /** Avoid Ant default near-black (#141414) — match App.js CSS custom properties. */
            algorithm: darkAlgorithm,
            token: {
              colorPrimary: BRAND.primary,
              colorBgBase: DARK.bgBase,
              colorBgLayout: DARK.bgBase,
              colorBgContainer: DARK.bgSurface,
              colorBgElevated: DARK.modalSurface,
              colorBorder: DARK.bgSubtle,
              colorBorderSecondary: DARK.bgSubtle,
              colorText: "#ffffff",
              colorTextSecondary: DARK.textSecondary,
              colorTextTertiary: DARK.textMuted,
              colorFillAlter: "rgba(255, 255, 255, 0.04)",
              colorFillSecondary: "rgba(255, 255, 255, 0.06)",
              colorFillTertiary: "rgba(255, 255, 255, 0.04)",
            },
            components: {
              Table: {
                headerBg: DARK.bgElevated,
                rowHoverBg: "rgba(255, 255, 255, 0.04)",
                borderColor: DARK.bgSubtle,
                headerColor: "#ffffff",
                colorBgContainer: DARK.bgElevated,
              },
              Select: {
                selectorBg: DARK.bgSurface,
                colorBgElevated: DARK.bgElevated,
                optionSelectedBg: brandAlpha(0.2),
                optionActiveBg: brandAlpha(0.12),
              },
              Input: {
                colorBgContainer: DARK.bgSurface,
                activeBorderColor: BRAND.primary,
                hoverBorderColor: DARK.bgSubtle,
              },
              InputNumber: {
                colorBgContainer: DARK.bgSurface,
              },
              Button: {
                defaultBg: DARK.bgElevated,
                defaultBorderColor: DARK.bgSubtle,
                defaultColor: "#ffffff",
                defaultHoverBg: DARK.bgSubtle,
                defaultHoverBorderColor: "#2A2A4A",
                defaultHoverColor: "#ffffff",
              },
              Drawer: {
                colorBgElevated: DARK.bgSurface,
              },
              Modal: {
                contentBg: DARK.popupSurface,
                headerBg: "transparent",
                footerBg: "transparent",
                borderRadiusLG: 18,
                boxShadow: "0 28px 80px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.04)",
              },
              Tabs: {
                inkBarColor: BRAND.primary,
                itemSelectedColor: "#ffffff",
                itemHoverColor: "#ffffff",
                itemColor: DARK.textMuted,
              },
              Empty: {
                colorTextDescription: DARK.textMuted,
              },
              Tooltip: {
                colorBgSpotlight: DARK.bgElevated,
                colorTextLightSolid: "#ffffff",
              },
            },
          }
        : {
            token: {
              colorPrimary: theme === "brand" ? "#e60000" : BRAND.primary,
              colorBgBase: "#ffffff",
              colorBgLayout: "#ffffff",
              colorBgContainer: "#ffffff",
              colorBgElevated: "#ffffff",
              colorBorder: "#E8E0F4",
              colorBorderSecondary: "#E8E0F4",
              colorText: "rgba(0, 0, 0, 0.88)",
              colorTextSecondary: "#6b6f8a",
              colorTextTertiary: "#6b6f8a",
              colorFillAlter: "rgba(107, 111, 138, 0.06)",
              colorFillSecondary: "rgba(107, 111, 138, 0.08)",
              colorFillTertiary: "rgba(107, 111, 138, 0.06)",
            },
            components: {
              Tooltip: {
                colorBgSpotlight: "#ffffff",
                colorTextLightSolid: "rgba(0, 0, 0, 0.88)",
              },
              Menu: {
                itemBorderRadius: 8,
                subMenuItemBorderRadius: 8,
                activeBarBorderWidth: 0,
              },
              Table: {
                headerBg: "#F5F4FF",
                rowHoverBg: brandAlpha(0.06),
                borderColor: "#E8E0F4",
                headerColor: "rgba(0, 0, 0, 0.88)",
                colorBgContainer: "#ffffff",
              },
              Select: {
                selectorBg: "#ffffff",
                colorBgElevated: "#ffffff",
                optionSelectedBg: brandAlpha(0.1),
                optionActiveBg: brandAlpha(0.06),
              },
              Input: {
                colorBgContainer: "#ffffff",
                activeBorderColor: BRAND.primary,
                hoverBorderColor: "#E8E0F4",
              },
              InputNumber: {
                colorBgContainer: "#ffffff",
              },
              Button: {
                defaultBg: "#ffffff",
                defaultBorderColor: "#E8E0F4",
                defaultColor: "rgba(0, 0, 0, 0.88)",
                defaultHoverBg: "#F5F4FF",
                defaultHoverBorderColor: BRAND.primary,
                defaultHoverColor: BRAND.primary,
              },
              Drawer: {
                colorBgElevated: "#ffffff",
              },
              Modal: {
                contentBg: "#ffffff",
                headerBg: "#ffffff",
              },
              Tabs: {
                inkBarColor: BRAND.primary,
                itemSelectedColor: BRAND.primary,
                itemHoverColor: "rgba(0, 0, 0, 0.88)",
                itemColor: "#6b6f8a",
              },
              Empty: {
                colorTextDescription: "#6b6f8a",
              },
              Segmented: {
                itemSelectedBg: "#ffffff",
                itemHoverBg: brandAlpha(0.06),
                trackBg: "#F5F4FF",
              },
            },
          },
    [theme]
  );

  return (
    <CopilotShellProvider
      copilotOpen={copilotOpen}
      setCopilotOpen={setCopilotOpen}
      hasCxGptAccess={cxGptAccess.hasAccess}
      cxGptAccessLoading={cxGptAccess.loading}
      advisorPlan={cxGptAccess.advisorPlan}
      advisorPlanName={cxGptAccess.advisorPlanName}
      advisorIsPro={cxGptAccess.advisorIsPro}
      advisorTrial={cxGptAccess.advisorTrial}
    >
      <ConfigProvider theme={consoleAntdTheme} locale={antdLocale}>
      <div className="consoleLayoutRoot" onClick={() => setOpenRightClickPopup(false)}>
        <div
          className={`consoleLayoutRoot-sideMenu ${!leftSidebarOpen ? "collapsed" : ""}${
            theme === "light" ? " consoleLayoutRoot-sideMenu--light" : ""
          }`}
          style={{
            backgroundColor:
              theme === "light" ? "#F5F4FF" : themeLayoutColor(theme),
          }}
        >
          <ConsoleSideMenu
            user={props.user}
            openRightClickPopup={openRightClickPopup}
            setOpenRightClickPopup={setOpenRightClickPopup}
            theme={theme}
            collapsed={!leftSidebarOpen}
            onToggleCollapse={toggleSidebarCollapse}
            hasAdvisorAccess={cxGptAccess.hasAccess}
            advisorAccessReady={!cxGptAccess.loading}
          />
        </div>

        <StableContent
          advisorFullPage={isAdvisorFullPage}
          prospectPage={isProspectPage}
        >
          {children}
        </StableContent>

        {!isAdvisorFullPage && copilotOpen && (
          <div
            className="consoleLayoutRoot-copilotColumn"
            style={{ width: copilotWidth }}
          >
            <div
              className="consoleLayoutRoot-copilotResize"
              onMouseDown={onCopilotResizeStart}
              role="separator"
              aria-orientation="vertical"
              aria-label={t("cx_gpt.resize_panel")}
            />
            <div className="consoleLayoutRoot-copilot">
              <GlobalCopilotPanel
                onClose={() => setCopilotOpen(false)}
                theme={theme}
                onToggleWide={onCopilotToggleWide}
                onOpenFullPage={() => {
                  history.push("/console/advisor");
                  setCopilotOpen(false);
                }}
                accountUser={props.user}
              />
            </div>
          </div>
        )}

        {!isAdvisorFullPage && !copilotOpen && (
          <button
            className={`consoleLayoutRoot-copilotToggle consoleLayoutRoot-copilotToggle--${theme}${
              isProspectPage ? " consoleLayoutRoot-copilotToggle--dock" : ""
            }`}
            onClick={() => setCopilotOpen(true)}
            title={t("cx_gpt.open_panel")}
          >
            <span className="consoleLayoutRoot-copilotToggleInner">
              <AdvisorIcon size="xs" alt="" />
              <span className="consoleLayoutRoot-copilotToggleLabel">
                {t("cx_gpt.nav_advisor")}
              </span>
            </span>
          </button>
        )}
        {!isAdvisorFullPage && cxGptAccess.hasAccess && (
          <AdvisorPopup
            userId={props.user?.uid}
            onOpen={() => setCopilotOpen(true)}
          />
        )}
        {/* {window.innerWidth > 768 && <DashboardGuide theme={theme} />} */}
      </div>
      </ConfigProvider>
    </CopilotShellProvider>
  );
};

export default ConsoleLayout;
